import React, { useState } from 'react';
import { Shield, Info, CheckCircle, AlertCircle } from 'lucide-react';

interface ConsentSetting {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  required: boolean;
}

const ConsentSettingsTab: React.FC = () => {
  const [consentSettings, setConsentSettings] = useState<ConsentSetting[]>([
    {
      id: 'data-collection',
      name: 'Data Collection Consent',
      description: 'Require explicit consent before collecting user data',
      enabled: true,
      required: true,
    },
    {
      id: 'analytics-tracking',
      name: 'Analytics Tracking',
      description: 'Allow tracking for analytics and performance monitoring',
      enabled: false,
      required: false,
    },
    {
      id: 'marketing-communications',
      name: 'Marketing Communications',
      description: 'Enable consent for marketing emails and communications',
      enabled: false,
      required: false,
    },
    {
      id: 'third-party-sharing',
      name: 'Third-party Data Sharing',
      description: 'Allow sharing data with third-party services and partners',
      enabled: false,
      required: false,
    },
  ]);

  const handleToggleSetting = (id: string) => {
    setConsentSettings(prev =>
      prev.map(setting =>
        setting.id === id && !setting.required
          ? { ...setting, enabled: !setting.enabled }
          : setting
      )
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Consent Settings</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Configure consent requirements for data collection and processing
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                Privacy Compliance
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                These settings help ensure compliance with privacy regulations like GDPR, CCPA, and other data protection laws. 
                Required settings cannot be disabled as they are essential for legal compliance.
              </p>
            </div>
          </div>
        </div>

        {/* Consent Settings List */}
        <div className="space-y-4">
          {consentSettings.map((setting) => (
            <div
              key={setting.id}
              className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    {setting.name}
                  </h3>
                  {setting.required && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                      <AlertCircle className="h-3 w-3" />
                      Required
                    </span>
                  )}
                  {setting.enabled && !setting.required && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                      <CheckCircle className="h-3 w-3" />
                      Active
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {setting.description}
                </p>
              </div>

              <div className="ml-4">
                <button
                  onClick={() => handleToggleSetting(setting.id)}
                  disabled={setting.required}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                    setting.enabled
                      ? 'bg-blue-600'
                      : 'bg-gray-200 dark:bg-gray-700'
                  } ${
                    setting.required
                      ? 'opacity-75 cursor-not-allowed'
                      : 'cursor-pointer'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      setting.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Settings */}
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Additional Configuration
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Consent Banner Position
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="bottom">Bottom of page</option>
                <option value="top">Top of page</option>
                <option value="modal">Modal overlay</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Consent Expiry (days)
              </label>
              <input
                type="number"
                defaultValue={365}
                min={1}
                max={3650}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="365"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Privacy Policy URL
            </label>
            <input
              type="url"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="https://yoursite.com/privacy-policy"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-8 flex justify-end">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors">
            Save Consent Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentSettingsTab;
